// =============================================================================
// SubbieComplianceWarning — T-319 Slice 3 (warn, don't block)
// Drop-in banner for any place a subbie/supplier is picked for work (booking
// forms, crew assignment). Given a supplier_id it looks up v_subbie_compliance;
// if that supplier is a subcontractor whose standing isn't clean (not approved,
// insurance expired/missing/expiring) it shows a warning with the specific
// issue and a link to the compliance centre. It NEVER blocks — a manager can
// still proceed. Renders nothing for non-subbie suppliers or clean subbies.
// =============================================================================

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ShieldAlert, ShieldCheck, ExternalLink } from "lucide-react";
import { supabase } from "@/lib/supabase";

type State = "pending_approval" | "no_insurance" | "expired" | "expiring" | "ok";

const MESSAGE: Record<Exclude<State, "ok">, string> = {
  pending_approval: "This subbie is not approved yet.",
  no_insurance: "No insurance on file for this subbie.",
  expired: "This subbie's insurance has expired.",
  expiring: "This subbie's insurance expires within 30 days.",
};

export default function SubbieComplianceWarning({
  supplierId,
  className = "",
}: {
  supplierId: string | null | undefined;
  className?: string;
}) {
  const { data } = useQuery({
    queryKey: ["subbie_compliance_by_supplier", supplierId],
    enabled: !!supplierId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_subbie_compliance")
        .select("subcontractor_company_id, legal_name, state")
        .eq("supplier_id", supplierId!)
        .maybeSingle();
      if (error) throw error;
      return data as { subcontractor_company_id: string; legal_name: string; state: State } | null;
    },
  });

  if (!supplierId || !data) return null; // not a subbie, or clean lookup pending

  if (data.state === "ok") {
    return (
      <div className={`flex items-center gap-1.5 text-[11px] text-ok ${className}`}>
        <ShieldCheck size={12} /> Subbie approved &amp; insurances current.
      </div>
    );
  }

  const critical = data.state === "expired" || data.state === "no_insurance";
  return (
    <div
      className={`flex items-start gap-2 rounded-md px-3 py-2 text-[12px] border ${
        critical ? "bg-danger/10 border-danger/30 text-danger" : "bg-warn/10 border-warn/30 text-warn"
      } ${className}`}
    >
      <ShieldAlert size={14} className="flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="font-medium">{MESSAGE[data.state]}</div>
        <div className="opacity-90">
          You can still book them, but check their standing first.{" "}
          <Link to="/subbies/compliance" className="inline-flex items-center gap-0.5 underline">
            Compliance centre <ExternalLink size={10} />
          </Link>
        </div>
      </div>
    </div>
  );
}
